<template>
  <div class="text-text1">
    <p v-if="title" class="mb-2">{{title}}</p>
    <!-- Single row with Date, Start Time, End Time, and All-day checkbox -->
    <div class="flex gap-4 items-end mb-4">
      <!-- Job Date Field -->
      <div class="flex-1 relative" ref="datePickerContainer" style="min-height: 42px;">
        <!-- Collapsed Date Display -->
        <div
          @click.stop="toggleDatePicker"
          class="cursor-pointer px-3 py-2 border-b-2 border-gray2 hover:border-primary transition-colors bg-transparent"
        >
          <p class="text-text1 heading-5">{{ formattedDate }}</p>
        </div>
        <!-- Expanded Date Picker - Absolutely positioned with highest z-index -->
        <div v-show="isDatePickerExpanded" ref="datePickerDropdown" class="absolute top-full left-0 bg-white shadow-xl rounded-xl mt-1" style="z-index: 99999; box-shadow: 0 10px 40px rgba(0,0,0,0.15);">
          <DateTimePickerCustom
            v-model="visitStartDateTime"
            :key="visitStartDateTime"
            :title="''"
            :showOnlyDate="true"
            :hideGoToday="true"
            :hideBtn="true"
            :roundedCard="true"
            @input="updateDate()"
          />
        </div>
      </div>

      <!-- Start Time Field -->
      <div class="flex-1">
        <CustomTimePicker
          v-model="startTime"
          :time-options="timeOptions"
          placeholder="Start time"
          @input="setVisitTime"
          :disabled="isAllDay"
          :noBorder="true"
        />
      </div>

      <!-- End Time Field -->
      <div class="flex-1">
        <CustomTimePicker
          v-model="endTime"
          :time-options="endTimeOptions"
          placeholder="End time"
          @input="setVisitTime"
          :disabled="isAllDay"
          :noBorder="true"
        />
      </div>
    </div>
    <!-- All-day checkbox -->
    <div class="flex items-center pb-2">
      <input
        type="checkbox"
        v-model="isAllDay"
        id="all-day-event"
        class="h-4 w-4 text-primary focus:ring-primary border-gray-300 rounded cursor-pointer"
      />
      <label for="all-day-event" class="ml-2 text-text1 cursor-pointer whitespace-nowrap">All-day</label>
    </div>
    <p v-if="timeErr && !isAllDay" class="text-error text-sm -mt-2 mb-2">End time must be after start time</p>
    <div class="mb-4 mt-4 sm:w-6/12" v-if="showEmployeeSelection">
      <MultiSelect 
        :inputext="employeeSearchText" 
        :inputId="'empslectinpuid'"
        :lableText="'Assign to'"
        :placholderText="`Select Employee`"
        :autoFocus="false"
        :showArrow="true"
        :setReadOnly="false"
        :searchCallApi="false"
        :showPlus="false"
        :keyName="'fullName'"
        :idKey="'userDetailId'"
        :sectionId="'employeelistsecid'"
        :listId="'employeelistidb'"
        :items="employeeList"
        :selectedValues="selectedEmployee"
        @selectNewForVal="selectEmployee"
      />
    </div>
    <div v-if="showJobItemSelection && !isItLead">
      <JobItemSelect :unscheduleData="jobDetail"/>
    </div>
  </div>
</template>

<script>
import JobItemSelect from './JobItemSelect.vue'
import DateTimePickerCustom from '@/View/components/dateTimepicker'
import MultiSelect from '@/View/components/multipleSelectDrop.vue'
import CustomTimePicker from './timePicker.vue'
import moment from 'moment'
import MyJobApp from '@/api/MyJobApp.js'

export default {
  name: 'ScheduleVisit',
  components: {
    JobItemSelect,
    MultiSelect,
    DateTimePickerCustom,
    CustomTimePicker,
  },
  data() {
    return {
      activeUer: 0,
      isItLead: false,
      employeeSearchText: '',
      selectedEmployee: [],
      employeeList: [],
      timeErr: false,
      isAllDay: false,
      startTime: "11:30",
      endTime: "12:30",
      visitStartDateTime: '',
      visitEndDateTime: '',
      finalVisitList: [],
      isDatePickerExpanded: false,
      savedStartTime: null,
      savedEndTime: null,
      // wasAutoFullDayNextDay: false,
    }
  },
  props: ['assignUser', 'isLead', 'visitErr', 'isFromSchedule', 'sTime', 'eTime', 'isSelect', 'type', 'title', 'showEmployeeSelection', 'showJobItemSelection', 'jobDetail', 'isCreateJob', 'isShowJobSection', 'setDefaultTime'],
  computed: {
    timeOptions() {
      const times = [];
      const now = new Date();
      const selectedDate = moment(this.visitStartDateTime).startOf('day');
      const today = moment().startOf('day');

      for (let hour = 0; hour < 24; hour++) {
        for (let minute of [0, 15, 30, 45]) {
          const timeStr = `${hour.toString().padStart(2, '0')}:${minute.toString().padStart(2, '0')}`;

          if (!this.isAllDay) {
            // If selected date is today, skip times in the past
            if (selectedDate.isSame(today, 'day')) {
              const optionTime = new Date();
              optionTime.setHours(hour, minute, 0, 0);
              if (optionTime <= now) continue;
            }
            // If selected date is in the future, show times from startTime onwards
            else if (selectedDate.isAfter(today, 'day')) {
              if (this.startTime) {
                const [startHour, startMinute] = this.startTime.split(':').map(Number);
                const currentTimeInMinutes = hour * 60 + minute;
                const startTimeInMinutes = startHour * 60 + startMinute;

                // Skip times before the set startTime
                if (currentTimeInMinutes < startTimeInMinutes) continue;
              }
            }
          }

          const display = this.formatTimeDisplay(timeStr);
          times.push({ value: timeStr, display });
        }
      }
      return times;
    },
    endTimeOptions() {
      return this.timeOptions.map(time => {
        const duration = this.calculateDuration(this.startTime, time.value);
        return { ...time, duration };
      }).filter(time => !this.isInvalidEndTime(time.value));
    },
    formattedDate() {
      if (!this.visitStartDateTime) return 'Select Date';
      return moment(this.visitStartDateTime).format('dddd, MMMM DD, YYYY');
    }
  },
  created() {
    this.activeUer = JSON.parse(localStorage.getItem('jobUserInfo')).userDetailId
  },
  mounted() {
    if (this.jobDetail && this.jobDetail.isUnschedule) {
      this.getUnscheduleData();
    }
    if (this.isFromSchedule && this.isSelect) {
      this.visitStartDateTime = moment(new Date(this.sTime)).format('YYYY-MM-DD');
      this.visitEndDateTime = moment(new Date(this.eTime)).format('YYYY-MM-DD');
      this.setTimes(this.sTime, this.eTime);
      this.updateDate();
    } else if (this.sTime && this.eTime) {
      // Auto-select time when coming from newDashboard (Create new Job)
      this.visitStartDateTime = moment(new Date(this.sTime)).format('YYYY-MM-DD');
      this.visitEndDateTime = moment(new Date(this.eTime)).format('YYYY-MM-DD');
      this.setTimes(this.sTime, this.eTime);
      this.updateDate();
    } else {
      this.visitStartDateTime = moment(new Date()).format('YYYY-MM-DD');
      this.visitEndDateTime = moment(new Date()).format('YYYY-MM-DD');
      this.setHourTime();
    }
    this.getEmployeeList();
    // Add click outside handler
    document.addEventListener('click', this.handleClickOutside);

    // Listen for edit mode schedule times
    this.$root.$on('setEditModeScheduleTimes', this.handleEditModeScheduleTimes);
  },
  beforeDestroy() {
    // Remove click outside handler
    document.removeEventListener('click', this.handleClickOutside);
    // Remove event listener
    this.$root.$off('setEditModeScheduleTimes', this.handleEditModeScheduleTimes);
  },
  watch: {
    sTime: {
      handler(newVal) {
        if (newVal && this.eTime) {
          this.visitStartDateTime = moment(new Date(newVal)).format('YYYY-MM-DD');
          this.visitEndDateTime = moment(new Date(this.eTime)).format('YYYY-MM-DD');
          this.setTimes(newVal, this.eTime);
          this.updateDate();
        }
      }
    },
    eTime: {
      handler(newVal) {
        if (newVal && this.sTime) {
          this.visitStartDateTime = moment(new Date(this.sTime)).format('YYYY-MM-DD');
          this.visitEndDateTime = moment(new Date(newVal)).format('YYYY-MM-DD');
          this.setTimes(this.sTime, newVal);
          this.updateDate();
        }
      }
    },
    isShowJobSection: {
      handler() {
        if (this.isShowJobSection && this.jobDetail) {
          this.visitStartDateTime = moment(new Date(this.jobDetail.visitList[0].visitStartDateTime)).format('YYYY-MM-DD');
          this.visitEndDateTime = moment(new Date(this.jobDetail.visitList[0].visitEndDateTime)).format('YYYY-MM-DD');
          this.setHourTime(this.setDefaultTime);
        }
      },
      deep: true
    },
    isLead: {
      handler() {
        this.isItLead = this.isLead;
      },
      deep: true
    },
    finalVisitList: {
      handler() {
        setTimeout(() => {
          this.$root.$emit('finalvisitlistHandler', this.finalVisitList);
        }, 500);
      }
    },
    timeOptions(newOptions) {
      if (!this.isAllDay && newOptions.length > 0 && !this.startTime) {
        // Only set defaults if times aren't already set
        this.startTime = newOptions[0].value;
        // Set end time to 1 hour after start time
        const endTimeOption = this.calculateEndTimeOneHourLater(newOptions[0].value);
        this.endTime = endTimeOption || newOptions[4]?.value || this.startTime;
      }
    },
    isAllDay: {
      handler(newVal) {
        if (newVal) {
          // Save current times before switching to all day (but only if not already all-day times)
          if (this.startTime !== '00:00' || this.endTime !== '23:59') {
            this.savedStartTime = this.startTime;
            this.savedEndTime = this.endTime;
          }

          this.startTime = '00:00';
          this.endTime = '23:59';
          this.setVisitTime();
        } else {
          // Restore times when unchecking All-day
          const hasSavedTimes = this.savedStartTime && this.savedEndTime &&
                                this.savedStartTime !== '00:00' && this.savedEndTime !== '23:59';

          if (hasSavedTimes) {
            // Restore previously saved times
            this.startTime = this.savedStartTime;
            this.endTime = this.savedEndTime;
          } else if (this.sTime && this.eTime) {
            // Edit mode: check if original times are not all-day
            const originalStartTime = moment(new Date(this.sTime)).format('HH:mm');
            const originalEndTime = moment(new Date(this.eTime)).format('HH:mm');

            if (originalStartTime !== '00:00' || originalEndTime !== '23:59') {
              // Restore from original job times
              this.setTimes(this.sTime, this.eTime);
            } else {
              // Original job was all-day, recalculate based on current time
              this.setHourTime(false);
              return; // setHourTime already calls updateDate
            }
          } else {
            // Create mode: recalculate based on current time
            this.setHourTime(false);
            return; // setHourTime already calls updateDate
          }
          this.setVisitTime();
        }
      }
    },
    startTime: {
      handler(newVal) {
        // Save time when manually changed (not during All Day mode)
        if (!this.isAllDay && newVal && newVal !== '00:00') {
          this.savedStartTime = newVal;
        }
        this.validateTimes();
        this.setVisitTime();
      }
    },
    endTime: {
      handler(newVal) {
        // Save time when manually changed (not during All Day mode)
        if (!this.isAllDay && newVal && newVal !== '23:59') {
          this.savedEndTime = newVal;
        }
        this.validateTimes();
        this.setVisitTime();
      }
    },
    visitStartDateTime: {
      handler(newDate) {
        // Force update to ensure DateTimePickerCustom reflects the new date
        // console.log('visitStartDateTime changed to:', newDate);
        this.$nextTick(() => {
          const selectedDate = moment(newDate).startOf('day');
          const tomorrow = moment().add(1, 'day').startOf('day');

          // Check if user selected tomorrow's date
          if (selectedDate.isSame(tomorrow, 'day')) {
            // console.log('User selected tomorrow — setting start time to 9:00 AM');
            this.startTime = '09:00';
            this.endTime = '10:00'; // optional — you can adjust as needed
            this.isAllDay = false; // ensure not all-day event
          }
          this.visitStartDateTime = selectedDate.format('YYYY-MM-DD');
          this.visitEndDateTime = selectedDate.format('YYYY-MM-DD');
          this.setVisitTime();
        });
      },
      immediate: true
    }
  },
  methods: {
    toggleDatePicker() {
      this.isDatePickerExpanded = !this.isDatePickerExpanded;
    },
    handleClickOutside(event) {
      // Close date picker when clicking outside
      if (this.isDatePickerExpanded && this.$refs.datePickerContainer) {
        if (!this.$refs.datePickerContainer.contains(event.target)) {
          this.isDatePickerExpanded = false;
        }
      }
    },
    calculateEndTimeOneHourLater(startTime) {
      if (!startTime) return null;
      const [hours, minutes] = startTime.split(':').map(Number);
      let endHour = hours + 1;
      if (endHour >= 24) endHour = 23; // Cap at 23:59
      return `${endHour.toString().padStart(2, '0')}:${minutes.toString().padStart(2, '0')}`;
    },
    formatTimeDisplay(time) {
      const [hours, minutes] = time.split(':').map(Number);
      const period = hours >= 12 ? 'PM' : 'AM';
      const displayHour = hours === 0 ? 12 : hours > 12 ? hours - 12 : hours;
      return `${displayHour}:${minutes.toString().padStart(2, '0')} ${period}`;
    },
    calculateDuration(start, end) {
      if (!start || !end) return '';
      const startDate = moment(`2023-01-01 ${start}`);
      const endDate = moment(`2023-01-01 ${end}`);
      if (!endDate.isAfter(startDate)) return '';
      
      const duration = moment.duration(endDate.diff(startDate));
      const hours = Math.floor(duration.asHours());
      const minutes = Math.floor(duration.asMinutes()) % 60;
      
      let durationStr = '';
      if (hours > 0) durationStr += `${hours} hr${hours > 1 ? 's' : ''}`;
      if (minutes > 0) durationStr += `${durationStr ? ' ' : ''}${minutes} min${minutes > 1 ? 's' : ''}`;
      return durationStr;
    },
    isInvalidEndTime(end) {
      if (!this.startTime || !end) return false;
      const startDate = moment(`2023-01-01 ${this.startTime}`);
      const endDate = moment(`2023-01-01 ${end}`);
      return !endDate.isAfter(startDate);
    },
    validateTimes() {
      this.timeErr = false;
      if (this.startTime && this.endTime && !this.isAllDay) {
        const start = moment(`2023-01-01 ${this.startTime}`);
        const end = moment(`2023-01-01 ${this.endTime}`);
        this.timeErr = !end.isAfter(start);
      }
    },
    setTimes(start, end) {
      this.startTime = moment(new Date(start)).format('HH:mm');
      this.endTime = moment(new Date(end)).format('HH:mm');

      // Save these as the original times for restoration
      if (!this.isAllDay) {
        this.savedStartTime = this.startTime;
        this.savedEndTime = this.endTime;
      }
    },
    parseDurationToMinutes(durationStr) {
      let hours = 0, minutes = 0;

      // Extract hours
      const hourMatch = durationStr.match(/(\d+)\s*hrs?/);
      if (hourMatch) {
        hours = parseInt(hourMatch[1], 10);
      }

      // Extract minutes
      const minuteMatch = durationStr.match(/(\d+)\s*mins?/);
      if (minuteMatch) {
        minutes = parseInt(minuteMatch[1], 10);
      }

      return (hours * 60) + minutes;
    },
    setVisitTime() {
      let visitDate = moment(this.visitStartDateTime).format('YYYY-MM-DD');
      let isAllDayEvent = this.isAllDay;
      let finalStartTime = this.startTime;
      let finalEndTime = this.endTime;

      // const totalMinutes = this.calculateDuration(finalStartTime, finalEndTime);
      // const estimateHr = this.parseDurationToMinutes(totalMinutes);

      this.finalVisitList = [{
        visitStartDateTime: new Date(`${visitDate} ${finalStartTime}`).toUTCString(),
        visitEndDateTime: new Date(`${visitDate} ${finalEndTime}`).toUTCString(),
        Error: this.timeErr,
        isUnschedule: false,
        visitEstimation: 0  // handled into the backend
      }];

      if (this.isAllDay !== isAllDayEvent) {
        this.isAllDay = isAllDayEvent;
      }

      if (visitDate !== moment(this.visitStartDateTime).format('YYYY-MM-DD')) {
        this.$set(this, 'visitStartDateTime', visitDate);
        this.$set(this, 'visitEndDateTime', visitDate);
      }

      // console.log('date and time', this.visitStartDateTime, this.startTime, this.endTime, estimateHr);
    },
    updateDate() {
      // Ensure visitEndDateTime is in sync with visitStartDateTime
      this.$set(this, 'visitEndDateTime', moment(this.visitStartDateTime).format('YYYY-MM-DD'));
      this.setVisitTime();
      // Collapse the date picker after selection
      this.$nextTick(() => {
        this.isDatePickerExpanded = false;
      });
    },
    // setHourTime(isSeDefault) {
    //   if (isSeDefault) {
    //     this.setTimes(this.sTime, this.eTime);
    //   } else {
    //     const now = new Date();
    //     let hours = now.getHours();
    //     let minutes = Math.ceil(now.getMinutes() / 15) * 15;
    //     if (minutes === 60) {
    //       hours++;
    //       minutes = 0;
    //     }
    //     this.startTime = `${hours.toString().padStart(2, '0')}:${minutes.toString().padStart(2, '0')}`;
    //     this.endTime = `${(hours + 1).toString().padStart(2, '0')}:${minutes.toString().padStart(2, '0')}`;
    //   }
    //   this.updateDate();
    // },
    setHourTime(isSeDefault) {
      if (isSeDefault) {
        this.setTimes(this.sTime, this.eTime);
      } else {
        const now = new Date();

        let hours = now.getHours();
        let minutes = now.getMinutes();

        // Round up to the next 15-minute interval
        minutes = Math.ceil(minutes / 15) * 15;
        if (minutes === 60) {
          hours++;
          minutes = 0;
        }

        const startHourStr = hours.toString().padStart(2, '0');
        const startMinStr = minutes.toString().padStart(2, '0');

        const endHour = hours + 1;
        const endHourStr = endHour.toString().padStart(2, '0');

        this.startTime = `${startHourStr}:${startMinStr}`;
        this.endTime = `${endHourStr}:${startMinStr}`;

        // Save these as the original times for restoration
        if (!this.isAllDay) {
          this.savedStartTime = this.startTime;
          this.savedEndTime = this.endTime;
        }
      }

      this.updateDate();
    },
    selectEmployee(emplist) {
      this.selectedEmployee = emplist.map(data => {
        let firstInitial = '';
        let lastName = '';

        if (data.firstName && data.lastName) {
          // Standard case — both names available
          firstInitial = data.firstName.charAt(0).toUpperCase() + '.';
          lastName = data.lastName;
        } else if (data.fullName) {
          // Handle case where only fullName is provided (e.g. "John Doe")
          const parts = data.fullName.trim().split(' ');
          if (parts.length > 1) {
            firstInitial = parts[0].charAt(0).toUpperCase() + '.';
            lastName = parts.slice(1).join(' ');
          } else {
            // Single name fallback
            lastName = data.fullName;
          }
        }

        return {
          userDetailId: data.userDetailId,
          fullName: `${firstInitial} ${lastName}`.trim()
        };
      });

      this.$root.$emit(
        'setSelectedEmpHandler',
        emplist.map(data => ({ userDetailId: data.userDetailId }))
      );
    },    
    getUnscheduleData() {
      this.$store.dispatch("SetLoader", {status: true, message: "Loading..."});
      MyJobApp.getUnscheduleDetail(
        this.jobDetail.jobId,
        (response) => {
          this.$root.$emit('getUnscheduleObject', response.Data);
          const temp = response.Data.assignToList.map(item => ({
            ...item,
            fullName: `${item.firstName} ${item.lastName}`
          }));
          this.selectEmployee(temp);
          this.$store.dispatch('SetLoader', {status: false, message: ''});
        },
        (error) => {
          this.$store.dispatch('SetAlert', {showAlert: true, message: error.message, color: 'error'});
          this.$store.dispatch('SetLoader', {status: false, message: ''});
        }
      );
    },
    getEmployeeList() {
      MyJobApp.getEmployeeList(
        'active',
        response => {
          const tempList = response.Data.tableRow?.map(el => ({
            ...el,
            fullName: `${el.firstName} ${el.lastName}`
          })) || [];
          
          this.employeeList = tempList;
          
          if (this.isCreateJob) {
            const defaultEmployee = tempList.find(el => el.userDetailId === (this.assignUser || this.activeUer));
            if (defaultEmployee) {
              const formattedName = `${defaultEmployee.firstName ? defaultEmployee.firstName.charAt(0).toUpperCase() + '.' : ''} ${defaultEmployee.lastName} (me)`;
              this.selectedEmployee = [{
                userDetailId: defaultEmployee.userDetailId,
                fullName: formattedName.trim()
              }];
              this.$root.$emit('setSelectedEmpHandler', [{ userDetailId: defaultEmployee.userDetailId }]);
            }
          }
          
          this.$store.dispatch('SetLoader', {status: false, message: ''});
        },
        () => {
          this.$store.dispatch('SetLoader', {status: false, message: ''});
        }
      );
    },
    handleEditModeScheduleTimes(data) {
      // Handle schedule times set from parent in edit mode
      if (data && data.startTime && data.endTime) {
        this.visitStartDateTime = moment(new Date(data.startTime)).format('YYYY-MM-DD');
        this.visitEndDateTime = moment(new Date(data.endTime)).format('YYYY-MM-DD');

        // Check if this is an all-day event (00:00 to 23:59)
        const startHM = moment(new Date(data.startTime)).format('HH:mm');
        const endHM = moment(new Date(data.endTime)).format('HH:mm');
        const isAllDayJob = startHM === '00:00' && endHM === '23:59';

        if (isAllDayJob) {
          // Don't save all-day times as original times
          this.isAllDay = true;
          this.startTime = '00:00';
          this.endTime = '23:59';
          // Clear saved times so we fall back to recalculating when unchecked
          this.savedStartTime = null;
          this.savedEndTime = null;
        } else {
          // Normal times - set and save them
          this.setTimes(data.startTime, data.endTime);
        }

        this.updateDate();
      }
    }
  }
}
</script>

<style scoped>
.custom-time-picker {
  @apply bg-gray1 rounded-xl border border-gray-300;
}
.custom-time-picker input {
  @apply text-text1 w-full px-3 py-2;
}
.custom-time-picker ul {
  @apply bg-gray1 border border-gray-300 rounded-xl max-h-60 overflow-y-auto;
}
.custom-time-picker li {
  @apply px-3 py-2 hover:bg-gray-200 cursor-pointer;
}
</style>